import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound, Forbidden } from "@/pages/ErrorPages";
import { formatMoney } from "@/lib/money";
import { Loader2, AlertTriangle } from "lucide-react";

export default function Checkout() {
  const { slug, sp } = useStore();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [orderInfo, setOrderInfo] = useState(null);
  const [provider, setProvider] = useState("mock");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const type = params.get("type"); // sub | rent | buy
  const planId = params.get("plan");
  const contentId = params.get("content");

  useEffect(() => {
    api.get(`/public/tenants/${slug}`)
      .then((r) => { setTenant(r.data); setPageStatus("ready"); setProvider(r.data.payment_provider_preference || "mock"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug]);

  useEffect(() => {
    if (!tenant) return;
    if (type === "sub" && planId) {
      api.get(`/public/tenants/${slug}/plans`)
        .then((r) => {
          const p = r.data.plans.find((x) => x.id === planId);
          if (p) setOrderInfo({ kind: "subscription", label: `${p.name} (${p.interval})`, amount: p.price_amount, currency: p.currency, plan: p });
          else setError("Plan not found.");
        })
        .catch(() => setError("Could not load plan."));
    } else if ((type === "rent" || type === "buy") && contentId) {
      api.get(`/cms/content/${contentId}`).catch(() => null); // (auth-only — fall through)
      // Fetch via public detail since contentId is a content_item_id, but public uses slug — workaround:
      // We'll load the content slug from content_items for tenant; simplest: ask /me/watch-access (auth required)
      // But that 402s. So: pass content_slug + content_id together. Caller path uses ?content=ID&content_slug=SLUG.
      const contentSlug = params.get("content_slug");
      if (contentSlug) {
        api.get(`/public/tenants/${slug}/content/${contentSlug}`)
          .then((r) => {
            const c = r.data.content;
            const price = type === "rent" ? c.tvod_rent_price : c.tvod_buy_price;
            if (!price) {
              setError(`No ${type} price set on this title.`);
              return;
            }
            setOrderInfo({
              kind: type === "rent" ? "tvod_rent" : "tvod_buy",
              label: `${type === "rent" ? "Rent" : "Buy"} · ${c.title}`,
              amount: price.amount,
              currency: price.currency,
              content: c,
            });
          })
          .catch(() => setError("Could not load title."));
      } else {
        setError("Missing content_slug query param.");
      }
    } else {
      setError("Invalid checkout request.");
    }
  }, [tenant, type, planId, contentId, slug, params]);

  if (pageStatus === "loading") return null;
  if (pageStatus === "not_found" || !tenant) return <NotFound />;
  if (status !== "authenticated" || !user) {
    return <Navigate to={sp(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)} replace />;
  }
  if (user.role === "subscriber" && user.tenant_id !== tenant.id) return <Forbidden />;

  const onPay = async () => {
    setError("");
    setSubmitting(true);
    try {
      let payload;
      if (orderInfo.kind === "subscription") {
        payload = await api.post("/checkout/subscription", { plan_id: planId, provider });
      } else {
        const kind = orderInfo.kind === "tvod_rent" ? "rent" : "buy";
        payload = await api.post("/checkout/tvod", { content_item_id: contentId, kind, provider });
      }
      // Redirect to mock checkout
      navigate(payload.data.checkout_url, { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-16" data-testid="checkout-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Checkout</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Order summary</h1>

        {error && (
          <div className="mt-6 text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="checkout-error">
            {error}
          </div>
        )}

        {orderInfo && (
          <div className="mt-8 bg-[#121212] border border-white/10 p-6" data-testid="checkout-order">
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{orderInfo.kind.replace("_", " ")}</div>
                <div className="font-display font-bold text-xl tracking-tight mt-1" data-testid="checkout-label">{orderInfo.label}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-3xl" data-testid="checkout-amount">{formatMoney(orderInfo.amount, orderInfo.currency)}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">total · {orderInfo.currency}</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Pay with</div>
              <div className="grid grid-cols-3 gap-2" data-testid="checkout-provider-picker">
                {["razorpay", "stripe", "mock"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setProvider(p)}
                    className={`px-3 py-3 text-xs uppercase tracking-[0.25em] border transition-colors ${provider === p ? "text-black bg-white border-white" : "text-white/70 hover:text-white border-white/15"}`}
                    data-testid={`checkout-provider-${p}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/80">
                <AlertTriangle className="w-3 h-3" /> Mock mode — selecting Razorpay or Stripe still routes to mock checkout in MVP.
              </div>
            </div>

            <button
              onClick={onPay}
              disabled={submitting}
              className="mt-6 w-full py-4 text-xs uppercase tracking-[0.25em] font-medium transition-transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: tenant.theme_color, color: "#0a0a0a" }}
              data-testid="checkout-pay-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay ${formatMoney(orderInfo.amount, orderInfo.currency)} with ${provider}`}
            </button>
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
